import React, { useCallback, useMemo, lazy, Suspense } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import { deleteCookie, inIframe, useEffectOnce, usePathParam, useViewport } from '@chilecompra/react-kit'
import { Button } from '@chilecompra/react-kit/components'
import {
  REACT_APP_BASE_URL,
  REACT_APP_MERCADO_PUBLICO_BASE_URL,
  REACT_APP_FEATURE_FLAG_GENERAL_INFORMATION_TAB,
  REACT_APP_FEATURE_FLAG_COURT_TAB,
  REACT_APP_FEATURE_FLAG_CLAIMS_TAB,
  REACT_APP_FEATURE_FLAG_PURCHASE_ORDERS_TAB,
  REACT_APP_FEATURE_FLAG_ANNUAL_PURCHASING_PLAN_TAB
} from '../config/settings/environment'
import { PATH_PARAM } from '../config/settings/regexes'
import { USER_TYPE, GESTION_TITLE } from '../config/settings/constant'
import routes from '../config/settings/routes'
import { initializeRecaptcha } from '../modules/hooks/recaptcha'
import AnnualPurchasePlanSection from '../containers/AnnualPurchasePlanSection/AnnualPurchasePlanSection.container'
import AuthorizationContext from '../containers/AuthorizationProvider/AuthorizationProvider.context'
import AuthorizationProviderContainer from '../containers/AuthorizationProvider/AuthorizationProvider.container'
import BasicBuyerInformationSectionContainer from '../containers/BasicBuyerInformationSection/BasicBuyerInformationSection.container'
import BuyerTabWrapper from '../containers/BuyerTabWrapper/BuyerTabWrapper.container'
import DependentBuyerSection from '../containers/DependentBuyerSection/DependentBuyerSection.container'
import ClaimsReceivedSection from '../containers/ClaimsReceivedSection/ClaimsReceivedSection.container'
import HeaderContainer from '../containers/Header/Header.container'
import HeadingSectionContainer from '../containers/HeadingSection/HeadingSection.container'
import InstitutionalManagementSection from '../containers/InstitutionalManagementSection/InstitutionalManagementSection.container'
import SummarySectionContainer from '../containers/SummarySection/SummarySection.container'
import OpenDataInformationButtonContainer from '../containers/OpenDataInformationSection/OpenDataInformationButton.container'
import BrandLayout from '../components/Layouts/BrandLayout'
import ProtectByFeatures from '../components/Security/ProtectByFeatures'
import PublicFooterContainer from '../containers/Footer/Public/PublicFooter.container'
import PublicProcurementCourtClaimsSection from '../containers/PublicProcurementCourtClaimsSection/PublicProcurementCourtClaimsSection.container'
import PurchaseOrdersSection from '../containers/PurchaseOrdersSection/PurchaseOrdersSection.container'
import OutsideAnnualPurchasePlanSection from '../containers/OutsideAnnualPurchasePlanSection/OutsideAnnualPurchasePlanSection.container'
import ClaimsReceivedChartSection from '../containers/ClaimsReceivedChartSection/ClaimsReceivedChartSection.container'
import { Column, Row } from './BuyerCard.styles'

const BehaviorSectionContainer = lazy(() => import('../containers/BehaviorSection/BehaviorSection.container'))

/**
 * The BuyerCard' page.
 */
const BuyerCard = () => {
  const { pathname } = useLocation()
  const dni = usePathParam('rut')
  const {
    size: { isUpToMedium }
  } = useViewport()

  const isInIframe = useMemo(() => {
    return inIframe()
  }, [])

  const breadcrumbs = useCallback((isPrivated, userType) => {
    return [
      {
        text: !isPrivated ? 'Home' : 'Escritorio',
        link: !isPrivated
          ? `${REACT_APP_MERCADO_PUBLICO_BASE_URL}/Home`
          : `${REACT_APP_MERCADO_PUBLICO_BASE_URL}/Portal/Modules/Menu/Menu.aspx`,
        isExternal: true,
        onClick: event => handleClickHome(event, isPrivated, userType)
      },
      userType !== 'Comprador' && {
        text: 'Búsqueda Comprador',
        link: `${REACT_APP_BASE_URL}/busqueda`,
        isExternal: true
      },
      { text: dni }
    ].filter(Boolean)
  }, [])

  const optionTabs = [
    (REACT_APP_FEATURE_FLAG_GENERAL_INFORMATION_TAB === true ||
      REACT_APP_FEATURE_FLAG_GENERAL_INFORMATION_TAB === 'true') && {
      label: 'Información general',
      content: (
        <>
          <InstitutionalManagementSection />
          <DependentBuyerSection />
        </>
      ),
      current: !!matchPath(routes.buyerCard, pathname) || !!matchPath(routes.generalInformation, pathname),
      route: routes.generalInformation.replace(PATH_PARAM, dni),
      show: () => true
    },
    {
      label: GESTION_TITLE,
      content: (
        <Suspense fallback={null}>
          <BehaviorSectionContainer />
        </Suspense>
      ),
      route: routes.behavior.replace(PATH_PARAM, dni),
      current: !!matchPath(routes.behavior, pathname),
      show: () => true,
      badge: { label: 'Piloto', color: 'primary' }
    },
    (REACT_APP_FEATURE_FLAG_ANNUAL_PURCHASING_PLAN_TAB === true ||
      REACT_APP_FEATURE_FLAG_ANNUAL_PURCHASING_PLAN_TAB === 'true') && {
      label: 'Plan anual de compras',
      content: (
        <>
          <AnnualPurchasePlanSection />
          <OutsideAnnualPurchasePlanSection />
        </>
      ),
      current: !!matchPath(routes.annualPurchasingPlan, pathname),
      route: routes.annualPurchasingPlan.replace(PATH_PARAM, dni),
      show: () => true
    },
    (REACT_APP_FEATURE_FLAG_PURCHASE_ORDERS_TAB === true || REACT_APP_FEATURE_FLAG_PURCHASE_ORDERS_TAB === 'true') && {
      label: 'Órdenes de compra',
      content: <PurchaseOrdersSection />,
      current: !!matchPath(routes.purchaseOrders, pathname),
      route: routes.purchaseOrders.replace(PATH_PARAM, dni),
      show: () => true
    },
    (REACT_APP_FEATURE_FLAG_CLAIMS_TAB === true || REACT_APP_FEATURE_FLAG_CLAIMS_TAB === 'true') && {
      label: 'Reclamos',
      content: (
        <>
          <ClaimsReceivedSection />
          <ClaimsReceivedChartSection />
        </>
      ),
      current: !!matchPath(routes.claims, pathname),
      route: routes.claims.replace(PATH_PARAM, dni),
      show: () => true
    },
    (REACT_APP_FEATURE_FLAG_COURT_TAB === true || REACT_APP_FEATURE_FLAG_COURT_TAB === 'true') && {
      label: 'Demandas Tribunal de Contratación Pública',
      content: <PublicProcurementCourtClaimsSection />,
      current: !!matchPath(routes.courtLawsuits, pathname),
      route: routes.courtLawsuits.replace(PATH_PARAM, dni),
      show: () => true
    }
  ].filter(Boolean)

  const handleClickHome = (event, isPrivated, userType) => {
    event.preventDefault()
    deleteCookie('access_token')
    if (isPrivated && userType === USER_TYPE.BUYER) {
      window.open(`${REACT_APP_MERCADO_PUBLICO_BASE_URL}/Portal/Modules/Menu/Menu.aspx`, '_top')
      return
    }
    location.href = `${REACT_APP_MERCADO_PUBLICO_BASE_URL}/Home`
  }

  const handleClickBack = (isPrivated, userType) => {
    if (isPrivated && userType === USER_TYPE.BUYER) {
      deleteCookie('access_token')
      window.open(`${REACT_APP_MERCADO_PUBLICO_BASE_URL}/Portal/Modules/Menu/Menu.aspx`, '_top')
      return
    }
    location.href = `${REACT_APP_BASE_URL}/busqueda`
  }

  useEffectOnce(() => {
    if (isInIframe) {
      window.parent.postMessage(
        {
          action: 'scrollTo',
          value: {
            x: 0,
            y: 0
          }
        },
        REACT_APP_MERCADO_PUBLICO_BASE_URL
      )
    } else {
      window.scrollTo(0, 0)
    }
  })

  useEffectOnce(() => {
    initializeRecaptcha()
  }, [])

  const handleBack = (isPrivated, userType) => () => handleClickBack(isPrivated, userType)

  return (
    <AuthorizationProviderContainer>
      <AuthorizationContext.Consumer>
        {({ isPrivated, userType }) => {
          return (
            <BrandLayout
              featured={<HeadingSectionContainer breadcrumbs={breadcrumbs(isPrivated && isInIframe, userType)} />}
              header={!(isPrivated && isInIframe) && <HeaderContainer />}
              footer={!(isPrivated && isInIframe) && <PublicFooterContainer />}
            >
              {isUpToMedium ? (
                <Row container margin="0" padding="0" id="LargeViewportPdf">
                  <Row item width="78%" maxWidth="100%">
                    <BasicBuyerInformationSectionContainer />
                    <BuyerTabWrapper prefix="buyer" options={optionTabs} />
                  </Row>
                  <Row item width="22%">
                    <ProtectByFeatures rule={features => features['com.buyer/summary.section.public']}>
                      <SummarySectionContainer />
                      <OpenDataInformationButtonContainer />
                    </ProtectByFeatures>
                  </Row>
                </Row>
              ) : (
                <Column id="SmallViewportPdf">
                  <Row padding="0" xs={12}>
                    <BasicBuyerInformationSectionContainer />
                  </Row>
                  <Column flexDirection="row" padding="0">
                    <ProtectByFeatures rule={features => features['com.buyer/summary.section.public']}>
                      <Row sm={6} padding="0">
                        <SummarySectionContainer />
                      </Row>
                      <Row xs={12} sm={5} padding="0" width="100%">
                        <OpenDataInformationButtonContainer />
                      </Row>
                    </ProtectByFeatures>
                  </Column>
                  <Row width="100%" padding="0">
                    <BuyerTabWrapper prefix="buyer" options={optionTabs} />
                  </Row>
                </Column>
              )}
              <Button
                color="default"
                variant="outlined"
                margin="20px 0 40px 0"
                onClick={handleBack(isPrivated && isInIframe, userType)}
                aria-label="Volver"
                tabIndex="0"
                width="190px"
              >
                Volver
              </Button>
            </BrandLayout>
          )
        }}
      </AuthorizationContext.Consumer>
    </AuthorizationProviderContainer>
  )
}
export default BuyerCard
